import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  ButtonBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
  Tooltip,
  Divider
} from '@material-ui/core';
import TaskIcon from '@mui/icons-material/Task';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import useAuth from '../../hooks/useAuth';
import { sessionApi } from '../../api/sessionApi';

const TaskNotificationsPopover = () => {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await sessionApi.fetchTaskNotifications(localStorage.getItem('accessToken'));
      if (data) {
        setNotifications(data);
        setUnreadCount(data.filter(n => Number(n.is_read) === 0).length);
      }
    } catch (err) {
      console.error('Erreur de récupération des notifications de tâches :', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Pool notifications every 60 seconds for live updates
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleOpen = () => {
    setOpen(true);
    fetchNotifications();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (Number(notif.is_read) === 0) {
        // Mark as read in DB
        await sessionApi.readNotification(notif.id, localStorage.getItem('accessToken'));
        
        // Update local state
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      handleClose();

      if (notif.type === 'task') {
        // Redirect to session details page with tasks tab activated
        navigate(`/dashboard/session/${notif.session_id}?tab=tasks`);

        // Trigger a window reload or route refresh if already on the page
        if (window.location.pathname === `/dashboard/session/${notif.session_id}`) {
          window.location.search = '?tab=tasks';
        }
      } else {
        // Notifications non liées à une tâche (désinscription, réclamation) : lien direct
        navigate(notif.link || '/dashboard');
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <Box
          component={ButtonBase}
          onClick={handleOpen}
          ref={anchorRef}
          sx={{
            alignItems: 'center',
            display: 'flex',
            position: 'relative',
            p: 1.2,
            borderRadius: '12px',
            bgcolor: open ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
            color: open ? 'primary.main' : 'text.secondary',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(99, 102, 241, 0.06)',
              color: 'primary.main',
            }
          }}
        >
          <TaskIcon sx={{ fontSize: 24 }} />
          {unreadCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 18,
                height: 18,
                backgroundColor: '#dc2626',
                borderRadius: '50%',
                border: '2px solid #fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '0.65rem',
                fontWeight: 800,
                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
              }}
            >
              {unreadCount}
            </Box>
          )}
        </Box>
      </Tooltip>

      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            width: 330,
            borderRadius: '16px',
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            mt: 1,
            p: 1.5,
          }
        }}
      >
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" sx={{ fontWeight: 700, bgcolor: 'rgba(99, 102, 241, 0.08)', color: 'primary.main', px: 1, py: 0.25, borderRadius: '8px' }}>
              {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 1 }} />

        {notifications.length === 0 ? (
          <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
              Aucune notification pour le moment.
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, maxHeight: 350, overflowY: 'auto' }}>
            {notifications.map((notif) => {
              const isUnread = Number(notif.is_read) === 0;
              const isTask = notif.type === 'task';
              const isDesinscription = notif.type === 'desinscription';
              const isReclamation = notif.type === 'reclamation';

              // Configuration d'affichage selon le type de notification.
              const accent = isDesinscription ? '#dc2626' : isReclamation ? '#d97706' : '#6366f1';
              const accentBg = isDesinscription
                ? 'rgba(220, 38, 38, 0.1)'
                : isReclamation ? 'rgba(217, 119, 6, 0.1)' : 'rgba(99, 102, 241, 0.1)';
              const icon = isDesinscription
                ? <PersonRemoveIcon sx={{ fontSize: '1.1rem' }} />
                : isReclamation
                  ? <PriorityHighIcon sx={{ fontSize: '1.1rem' }} />
                  : <AssignmentIcon sx={{ fontSize: '1.1rem' }} />;
              const title = isDesinscription
                ? (notif.title || 'Désinscription')
                : isReclamation
                  ? (notif.title || 'Affectation de réclamation')
                  : 'Affectation de tâche';

              return (
                <ListItem
                  key={notif.id}
                  button
                  onClick={() => handleNotificationClick(notif)}
                  sx={{
                    borderRadius: '10px',
                    py: 1.2,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    bgcolor: isUnread ? 'rgba(99, 102, 241, 0.02)' : 'transparent',
                    border: '1px solid',
                    borderColor: isUnread ? 'rgba(99, 102, 241, 0.06)' : 'rgba(226, 232, 240, 0.5)',
                    '&:hover': {
                      bgcolor: isUnread ? 'rgba(99, 102, 241, 0.06)' : 'rgba(15, 23, 42, 0.02)',
                      borderColor: isUnread ? 'rgba(99, 102, 241, 0.1)' : 'rgba(226, 232, 240, 0.8)',
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 32, mt: 0.25 }}>
                    <Avatar sx={{ bgcolor: isUnread ? accentBg : 'rgba(100, 116, 139, 0.08)', color: isUnread ? accent : 'text.secondary', width: 32, height: 32 }}>
                      {icon}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: isUnread ? 'text.primary' : 'text.secondary', lineHeight: 1.3 }}>
                          {title}
                        </Typography>
                        <Box sx={{
                          px: 1,
                          py: 0.25,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          borderRadius: '6px',
                          bgcolor: isUnread ? '#dc2626' : 'rgba(100, 116, 139, 0.08)',
                          color: isUnread ? '#fff' : 'text.secondary',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap'
                        }}>
                          {isUnread ? 'nouveau' : 'lu'}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {isTask ? (
                          <>
                            <Typography sx={{ fontSize: '0.8rem', color: isUnread ? 'text.secondary' : 'text.disabled', lineHeight: 1.4 }}>
                              Vous avez été affecté à la tâche <strong>{notif.task_name}</strong>.
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: isUnread ? 'primary.main' : 'text.disabled', fontWeight: 600, mt: 0.5 }}>
                              Session #{notif.number_session}
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: '0.8rem', color: isUnread ? 'text.secondary' : 'text.disabled', lineHeight: 1.4 }}>
                            {notif.message}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};

export default TaskNotificationsPopover;
